import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

const TOKEN_KEY = "evoo_token";
export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (tok) => {
  if (tok) localStorage.setItem(TOKEN_KEY, tok);
  else localStorage.removeItem(TOKEN_KEY);
};

api.interceptors.request.use((config) => {
  const tok = getToken();
  if (tok) config.headers.Authorization = `Bearer ${tok}`;
  return config;
});

// Immediately end the session if the account was suspended/deleted by an admin.
api.interceptors.response.use(
  (r) => r,
  (err) => {
    const detail = err.response?.data?.detail;
    if (err.response?.status === 403 && (detail === "ACCOUNT_SUSPENDED" || detail === "ACCOUNT_DELETED")) {
      setToken(null);
      if (!window.location.pathname.startsWith("/connexion")) {
        window.location.href = "/connexion?suspended=1";
      }
    }
    return Promise.reject(err);
  }
);

export function formatApiError(detail) {
  if (detail == null) return null;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// public data
export const getRecentLots = (limit = 6) => api.get(`/lots/recent?limit=${limit}`).then((r) => r.data);
export const getLots = (type) => api.get(`/lots${type ? `?type=${type}` : ""}`).then((r) => r.data);
export const searchLots = (params) => api.get(`/lots`, { params }).then((r) => r.data);
export const getPrices = () => api.get(`/prices`).then((r) => r.data);
export const getMills = () => api.get(`/mills`).then((r) => r.data);
export const getDirectory = (params = {}) => api.get(`/directory`, { params }).then((r) => r.data);
export const getDirectoryFacets = () => api.get(`/directory/facets`).then((r) => r.data);
export const getDirectoryEntry = (id) => api.get(`/directory/${id}`).then((r) => r.data);
export const getArticles = () => api.get(`/articles`).then((r) => r.data);

// ---------- Blog / Content center ----------
export const getBlogMeta = () => api.get(`/blog/meta`).then((r) => r.data);
export const listBlogPosts = (params = {}) => api.get(`/blog/posts`, { params }).then((r) => r.data);
export const getBlogPost = (slug, lang) => api.get(`/blog/posts/${encodeURIComponent(slug)}`, { params: { lang } }).then((r) => r.data);
export const adminListBlog = () => api.get(`/admin/blog/posts`).then((r) => r.data);
export const adminGetBlog = (id) => api.get(`/admin/blog/posts/${id}`).then((r) => r.data);
export const adminCreateBlog = (body) => api.post(`/admin/blog/posts`, body).then((r) => r.data);
export const adminUpdateBlog = (id, body) => api.put(`/admin/blog/posts/${id}`, body).then((r) => r.data);
export const adminDeleteBlog = (id) => api.delete(`/admin/blog/posts/${id}`).then((r) => r.data);
export const adminGenerateBlog = (body) => api.post(`/admin/blog/generate`, body).then((r) => r.data);
export const adminTranslateBlog = (body) => api.post(`/admin/blog/translate`, body).then((r) => r.data);
export const blogMediaUrl = (path) => `${API}/blog/media/${path}`;
export const blogCoverSrc = (p) => (!p ? "" : /^https?:\/\//.test(p) ? p : blogMediaUrl(p));
export const uploadBlogCover = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post(`/admin/blog/cover`, fd).then((r) => r.data);
};
export const getStats = () => api.get(`/stats`).then((r) => r.data);

// auth
export const apiRegister = (body) => api.post(`/auth/register`, body).then((r) => r.data);
export const apiLogin = (body) => api.post(`/auth/login`, body).then((r) => r.data);
export const apiLogout = () => api.post(`/auth/logout`).then((r) => r.data);
export const apiMe = () => api.get(`/auth/me`).then((r) => r.data);
export const apiGoogleSession = (session_id) => api.post(`/auth/google/session`, { session_id }).then((r) => r.data);
export const apiCompleteProfile = (body) => api.post(`/auth/complete-profile`, body).then((r) => r.data);
export const confirmEmail = (token) => api.post(`/auth/confirm-email`, { token }).then((r) => r.data);
export const resendEmailConfirmation = (email, lang) => api.post(`/auth/resend-email-confirmation`, { email, lang }).then((r) => r.data);

// requester's own packaging quotes
export const getMyPackagingQuotes = () => api.get(`/my/packaging-quotes`).then((r) => r.data);

// in-app notifications
export const listNotifications = () => api.get(`/notifications`).then((r) => r.data);
export const getUnreadCount = () => api.get(`/notifications/unread-count`).then((r) => r.data);
export const markNotifRead = (id) => api.post(`/notifications/${id}/read`).then((r) => r.data);
export const markAllNotifRead = () => api.post(`/notifications/read-all`).then((r) => r.data);

// company & kyc
export const updateCompany = (body) => api.put(`/companies/me`, body).then((r) => r.data);
export const updateUserType = (user_type) => api.patch(`/users/me/type`, { user_type }).then((r) => r.data);
export const getKycDocs = () => api.get(`/kyc/documents`).then((r) => r.data);
export const uploadKyc = (docType, file) => {
  const fd = new FormData();
  fd.append("doc_type", docType);
  fd.append("file", file);
  return api.post(`/kyc/upload`, fd).then((r) => r.data);
};
export const submitKyc = () => api.post(`/kyc/submit`).then((r) => r.data);
export const fetchKycFileBlob = (docId) =>
  api.get(`/kyc/file/${docId}`, { responseType: "blob" }).then((r) => r.data);

// vrac marketplace
export const mediaUrl = (path) => `${API}/lots/media/${path}`;
export const photoSrc = (p) => (p && /^https?:\/\//.test(p) ? p : mediaUrl(p));
export const getLotDetail = (id) => api.get(`/lots/${id}`).then((r) => r.data);
export const getMyLots = () => api.get(`/lots/mine`).then((r) => r.data);
export const createLot = (body) => api.post(`/lots`, body).then((r) => r.data);
export const createPackagedLot = (body) => api.post(`/lots/conditionne`, body).then((r) => r.data);
export const createAlert = (body) => api.post(`/alerts`, body).then((r) => r.data);
export const confirmAlert = (token) => api.post(`/alerts/confirm`, { token }).then((r) => r.data);
export const resendConfirmation = (email, type) => api.post(`/alerts/resend-confirmation`, { email, type }).then((r) => r.data);
export const requestManageLink = (email) => api.post(`/alerts/manage-link`, { email }).then((r) => r.data);
export const getManageAlerts = (token) => api.get(`/alerts/manage/${token}`).then((r) => r.data);
export const updateManageAlert = (token, id, body) => api.put(`/alerts/manage/${token}/${id}`, body).then((r) => r.data);
export const deleteManageAlert = (token, id) => api.delete(`/alerts/manage/${token}/${id}`).then((r) => r.data);
export const updateLot = (id, body) => api.put(`/lots/${id}`, body).then((r) => r.data);
export const setLotStatus = (id, status) => api.patch(`/lots/${id}/status`, { status }).then((r) => r.data);
export const uploadLotMedia = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post(`/lots/media`, fd).then((r) => r.data);
};
export const getMyOffers = () => api.get(`/offers/mine`).then((r) => r.data);
export const getDeals = () => api.get(`/deals/mine`).then((r) => r.data);
export const getDashboardBadges = () => api.get(`/dashboard/badges`).then((r) => r.data);
// negotiations
export const startNegotiation = (body) => api.post(`/negotiations`, body).then((r) => r.data);
export const listNegotiations = () => api.get(`/negotiations`).then((r) => r.data);
export const getNegotiation = (id) => api.get(`/negotiations/${id}`).then((r) => r.data);
export const negoCounter = (id, body) => api.post(`/negotiations/${id}/offer`, body).then((r) => r.data);
export const negoMessage = (id, message) => api.post(`/negotiations/${id}/message`, { message }).then((r) => r.data);
export const negoAccept = (id, oid) => api.post(`/negotiations/${id}/offers/${oid}/accept`).then((r) => r.data);
export const negoRefuse = (id, oid) => api.post(`/negotiations/${id}/offers/${oid}/refuse`).then((r) => r.data);
export const getCommission = () => api.get(`/commission`).then((r) => r.data);
export const setCommission = (body) => api.put(`/admin/commission`, body).then((r) => r.data);
// admin back-office (users, kyc, lots, stats, settings)
export const getAdminStats = () => api.get(`/admin/stats`).then((r) => r.data);
export const getAdminTreasury = () => api.get(`/admin/treasury`).then((r) => r.data);
export const adminRunInvoiceReminders = () => api.post(`/admin/tools/run-invoice-reminders`).then((r) => r.data);
export const adminListUsers = () => api.get(`/admin/users`).then((r) => r.data);
export const adminConfirmEmail = (email) => api.post(`/admin/users/confirm-email`, { email }).then((r) => r.data);
export const getAdminVerifications = () => api.get(`/admin/verifications`).then((r) => r.data);
export const adminCompanyKycDocs = (companyId) => api.get(`/admin/verifications/${companyId}/documents`).then((r) => r.data);
export const adminDecideVerification = (companyId, body) => api.post(`/admin/verifications/${companyId}`, body).then((r) => r.data);
// password reset (public)
export const forgotPassword = (email, lang) => api.post(`/auth/forgot-password`, { email, lang }).then((r) => r.data);
export const resetPassword = (token, password) => api.post(`/auth/reset-password`, { token, password }).then((r) => r.data);
// admin user management
export const adminGetUser = (id) => api.get(`/admin/users/${id}`).then((r) => r.data);
export const adminSetUserStatus = (id, body) => api.patch(`/admin/users/${id}/status`, body).then((r) => r.data);
export const adminSetUserType = (id, user_type) => api.patch(`/admin/users/${id}/type`, { user_type }).then((r) => r.data);
export const adminAddUserNote = (id, text) => api.post(`/admin/users/${id}/notes`, { text }).then((r) => r.data);
export const adminResendUserConfirmation = (id) => api.post(`/admin/users/${id}/resend-confirmation`).then((r) => r.data);
export const adminSendPasswordReset = (id) => api.post(`/admin/users/${id}/reset-password`).then((r) => r.data);
export const adminUserActivity = (id, kind) => api.get(`/admin/users/${id}/activity/${kind}`).then((r) => r.data);
export const adminActivityItem = (kind, itemId) => api.get(`/admin/activity/${kind}/${itemId}`).then((r) => r.data);
export const adminDeleteUser = (id, mode = "soft") => api.delete(`/admin/users/${id}`, { params: { mode } }).then((r) => r.data);
export const adminListLots = (params = {}) => api.get(`/admin/lots`, { params }).then((r) => r.data);
export const adminSetLotStatus = (id, status) => api.patch(`/admin/lots/${id}/status`, { status }).then((r) => r.data);
export const adminLotDetail = (id) => api.get(`/admin/lots/${id}`).then((r) => r.data);
export const adminListDeals = (params = {}) => api.get(`/admin/deals`, { params }).then((r) => r.data);
export const getPlatformSettings = () => api.get(`/admin/settings/platform`).then((r) => r.data);
export const setPlatformSettings = (body) => api.put(`/admin/settings/platform`, body).then((r) => r.data);
export const getNotificationPrefs = () => api.get(`/users/me/notification-prefs`).then((r) => r.data);
export const updateNotificationPrefs = (body) => api.put(`/users/me/notification-prefs`, body).then((r) => r.data);
export const getFaq = () => api.get(`/faq`).then((r) => r.data);
export const adminListFaq = () => api.get(`/admin/faq`).then((r) => r.data);
export const adminCreateFaq = (body) => api.post(`/admin/faq`, body).then((r) => r.data);
export const adminUpdateFaq = (id, body) => api.put(`/admin/faq/${id}`, body).then((r) => r.data);
export const adminDeleteFaq = (id) => api.delete(`/admin/faq/${id}`).then((r) => r.data);
export const listRequests = (params = {}) => api.get(`/requests`, { params }).then((r) => r.data);
export const getMyRequests = () => api.get(`/requests/mine`).then((r) => r.data);
export const createRequest = (body) => api.post(`/requests`, body).then((r) => r.data);
export const updateRequestStatus = (id, status) => api.patch(`/requests/${id}`, { status }).then((r) => r.data);

// market data (prix & analyse)
export const getMarketSettings = () => api.get(`/market/settings`).then((r) => r.data);
export const getOriginPrices = (params = {}) => api.get(`/market/origin-prices`, { params }).then((r) => r.data);
export const getRetailPrices = (params = {}) => api.get(`/market/retail-prices`, { params }).then((r) => r.data);
export const getMarketBalance = (params = {}) => api.get(`/market/balance`, { params }).then((r) => r.data);
export const getThermometer = () => api.get(`/market/thermometer`).then((r) => r.data);
export const getWorldIndex = () => api.get(`/market/world-index`).then((r) => r.data);
export const getFeedStatus = () => api.get(`/market/feed-status`).then((r) => r.data);
export const adminSyncFeeds = () => api.post(`/admin/market/sync-feeds`).then((r) => r.data);
// admin market
export const adminSaveMarketSettings = (body) => api.put(`/admin/market/settings`, body).then((r) => r.data);
export const adminCreateOrigin = (b) => api.post(`/admin/market/origin-prices`, b).then((r) => r.data);
export const adminUpdateOrigin = (id, b) => api.put(`/admin/market/origin-prices/${id}`, b).then((r) => r.data);
export const adminDeleteOrigin = (id) => api.delete(`/admin/market/origin-prices/${id}`).then((r) => r.data);
export const adminAddOriginPoint = (id, b) => api.post(`/admin/market/origin-prices/${id}/points`, b).then((r) => r.data);
export const adminCreateRetail = (b) => api.post(`/admin/market/retail-prices`, b).then((r) => r.data);
export const adminUpdateRetail = (id, b) => api.put(`/admin/market/retail-prices/${id}`, b).then((r) => r.data);
export const adminDeleteRetail = (id) => api.delete(`/admin/market/retail-prices/${id}`).then((r) => r.data);
export const adminAddRetailPoint = (id, b) => api.post(`/admin/market/retail-prices/${id}/points`, b).then((r) => r.data);
export const adminCreateBalance = (b) => api.post(`/admin/market/balance`, b).then((r) => r.data);
export const adminUpdateBalance = (id, b) => api.put(`/admin/market/balance/${id}`, b).then((r) => r.data);
export const adminDeleteBalance = (id) => api.delete(`/admin/market/balance/${id}`).then((r) => r.data);
export const adminAddBalancePoint = (id, b) => api.post(`/admin/market/balance/${id}/points`, b).then((r) => r.data);

// tools: alerts, packaging quotes, inspection
export const createPackagingQuote = (body) => api.post(`/quotes/packaging`, body).then((r) => r.data);
export const getInspectionLabs = (params = {}) => api.get(`/inspection/labs`, { params }).then((r) => r.data);
export const createInspectionLead = (body) => api.post(`/inspection/leads`, body).then((r) => r.data);
export const createContactMessage = (body) => api.post(`/contact`, body).then((r) => r.data);
// admin tools
export const adminGetAlerts = () => api.get(`/admin/tools/alerts`).then((r) => r.data);
export const adminGetPackagingQuotes = () => api.get(`/admin/tools/packaging-quotes`).then((r) => r.data);
export const adminGetInspectionLeads = () => api.get(`/admin/tools/inspection-leads`).then((r) => r.data);
export const adminCreateLab = (b) => api.post(`/admin/inspection/labs`, b).then((r) => r.data);
export const adminUpdateLab = (id, b) => api.put(`/admin/inspection/labs/${id}`, b).then((r) => r.data);
export const adminDeleteLab = (id) => api.delete(`/admin/inspection/labs/${id}`).then((r) => r.data);
// packaging quote threads
export const sellerListQuotes = () => api.get(`/seller/packaging-quotes`).then((r) => r.data);
export const sellerGetQuote = (id) => api.get(`/seller/packaging-quotes/${id}`).then((r) => r.data);
export const sellerReplyQuote = (id, text) => api.post(`/seller/packaging-quotes/${id}/reply`, { text }).then((r) => r.data);
export const getQuoteThread = (token) => api.get(`/quotes/packaging/thread/${token}`).then((r) => r.data);
export const replyQuoteThread = (token, text) => api.post(`/quotes/packaging/thread/${token}/reply`, { text }).then((r) => r.data);
// structured quotes (devis structuré) — generic
export const uploadQuoteAttachment = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post(`/quotes/attachments`, fd).then((r) => r.data);
};
export const sellerSubmitQuote = (qid, body) => api.post(`/seller/packaging-quotes/${qid}/quote`, body).then((r) => r.data);
export const quoteThreadAction = (token, body) => api.post(`/quotes/packaging/thread/${token}/quote-action`, body).then((r) => r.data);
export const downloadBlob = (url, filename) =>
  api.get(url, { responseType: "blob" }).then((r) => {
    const href = URL.createObjectURL(new Blob([r.data], { type: r.data.type || "application/octet-stream" }));
    const a = document.createElement("a");
    a.href = href; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(href);
  });
export const adminSetQuoteStatus = (id, status) => api.patch(`/admin/tools/packaging-quotes/${id}/status`, { status }).then((r) => r.data);
export const adminSetLeadStatus = (id, status) => api.patch(`/admin/tools/inspection-leads/${id}/status`, { status }).then((r) => r.data);
export const adminSetLeadNote = (id, note) => api.patch(`/admin/tools/inspection-leads/${id}/note`, { note }).then((r) => r.data);
export const adminGetContactMessages = () => api.get(`/admin/contact-messages`).then((r) => r.data);
export const adminSetContactStatus = (id, status) => api.patch(`/admin/contact-messages/${id}/status`, { status }).then((r) => r.data);
export const adminSetContactNote = (id, note) => api.patch(`/admin/contact-messages/${id}/note`, { note }).then((r) => r.data);
export const adminConfirmAlert = (id) => api.patch(`/admin/tools/alerts/${id}/confirm`).then((r) => r.data);
export const adminResendAlertConfirmation = (id) => api.post(`/admin/tools/alerts/${id}/resend-confirmation`).then((r) => r.data);
export const adminDeleteAlert = (id) => api.delete(`/admin/tools/alerts/${id}`).then((r) => r.data);

// ---- Billing / commission invoices ----
export const adminGetBilling = () => api.get(`/admin/billing`).then((r) => r.data);
export const adminSaveBilling = (body) => api.put(`/admin/billing`, body).then((r) => r.data);
export const adminUserStatement = (userId) => api.get(`/admin/users/${userId}/statement`).then((r) => r.data);
export const adminListInvoices = (params) => api.get(`/admin/invoices`, { params }).then((r) => r.data);
export const exportInvoicesUrl = (params) => {
  const clean = Object.fromEntries(Object.entries(params || {}).filter(([, v]) => v !== undefined && v !== "" && v !== null));
  const qs = new URLSearchParams(clean).toString();
  return `/admin/invoices/export${qs ? `?${qs}` : ""}`;
};
export const priceDigestPreview = () => api.get(`/admin/tools/price-digest/preview`).then((r) => r.data);
export const priceDigestTest = (email) => api.post(`/admin/tools/price-digest/test`, { email }).then((r) => r.data);
export const adminSetInvoiceStatus = (id, body) => api.patch(`/admin/invoices/${id}/status`, body).then((r) => r.data);
export const adminInvoiceProofBlob = (id) => api.get(`/admin/invoices/${id}/proof`, { responseType: "blob" }).then((r) => r.data);
export const getMyInvoices = () => api.get(`/invoices/mine`).then((r) => r.data);
export const uploadPaymentProof = (id, formData) => api.post(`/invoices/${id}/payment-proof`, formData).then((r) => r.data);
export const openBlobInTab = (url) =>
  api.get(url, { responseType: "blob" }).then((r) => {
    const href = URL.createObjectURL(new Blob([r.data], { type: r.data.type || "application/pdf" }));
    window.open(href, "_blank");
    setTimeout(() => URL.revokeObjectURL(href), 60000);
  });

export const ORIGIN_COUNTRIES = ["tunisia", "spain", "italy", "greece", "portugal", "turkey", "morocco"];
export const ORIGIN_QUALITIES = ["extra_vierge", "vierge", "lampante"];
export const BALANCE_METRICS = ["production", "exports", "stocks", "consumption", "imports"];
export const MARKET_SOURCES = ["manual", "auto_eu", "auto_world"];

export const GOVERNORATES = [
  "Ariana", "Béja", "Ben Arous", "Bizerte", "Gabès", "Gafsa", "Jendouba", "Kairouan",
  "Kasserine", "Kébili", "Le Kef", "Mahdia", "Manouba", "Médenine", "Monastir", "Nabeul",
  "Sfax", "Sidi Bouzid", "Siliana", "Sousse", "Tataouine", "Tozeur", "Tunis", "Zaghouan",
];
export const INCOTERMS = ["EXW", "FCA", "FOB", "CFR", "CIF", "CPT", "CIP", "DAP", "DDP"];
export const QUALITIES = ["extra_vierge", "vierge", "lampante", "premium_bio", "grignon"];
export const PACKAGING_TYPES = ["verre", "pet", "bidon_metal", "brik", "canette", "monodose", "dosettes"];
export const VOLUMES = [125, 250, 500, 750, 1000, 3000, 5000];
